import React, {useEffect, useMemo, useState} from "react";
import styled from "styled-components";
import useRouter from "../../common/hooks/useRouter";
import Rating from "../../common/components/modules/Rating";
import {PrimaryLoadingButton} from "../../common/components/elements/button/PrimaryButton";
import {FormContainer} from "../../common/components/collections/form/forms";
import {Field, Form} from "react-final-form";
import * as Notifications from "src/core/notifications";
import useOrderReviewState from "src/core/checkout/hooks/useOrderReview";

/**
 * @return {boolean}
 */
function OrderRatingForm({
  orderId,
  onSuccess,
  submitText,
  readOnly,
  order,
  isOrderDetailsCheckout,
}) {
  const router = useRouter();
  const [orderReviewState, createOrderReview] = useOrderReviewState();
  const [requestedChange, setRequestedChange] = useState(false);

  const initialValues = useMemo(
    () =>
      order && order.hasReviews()
        ? {
            rating: order.getFirstReview().rating,
            comment: order.getFirstReview().comment,
          }
        : {
            rating: 5,
            comment: "",
          },
    [order]
  );

  function onSubmit(formValues) {
    if (!requestedChange) {
      createOrderReview(orderId, formValues.rating, formValues.comment);
      setRequestedChange(true);
    }
  }

  const {error, data} = orderReviewState;
  useEffect(() => {
    if (data && requestedChange) {
      Notifications.success("Thank you for your feedback");
      onSuccess();
      setRequestedChange(false);
    } else if (error) {
      const msg = typeof error === "string" ? error : "Failed to review your order";
      Notifications.error(msg);
      setRequestedChange(false);
    }
  }, [data, error, onSuccess, orderReviewState, requestedChange, router]);

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({handleSubmit}) => (
        <form onSubmit={handleSubmit}>
          <CenteredFormContainer isOrderDetailsCheckout={isOrderDetailsCheckout}>
            <Field
              name={"rating"}
              render={({input}) => (
                <Rating
                  placeholderRating={5}
                  readonly={readOnly}
                  initialRating={input.value}
                  onChange={value => input.onChange(value)}
                  size={isOrderDetailsCheckout && "28px"}
                />
              )}
            />
            {readOnly ? (
              initialValues.comment && <div>&quot;{initialValues.comment}&quot;</div>
            ) : (
              <Field
                name={"comment"}
                render={({input}) => (
                  <TextAreaInput
                    {...input}
                    placeholder={"Add your feedback here"}
                    rows={5}
                  />
                )}
              />
            )}
            {!readOnly && (
              <PrimaryLoadingButton
                type="submit"
                data-cy="rateOrderButton"
                label={submitText}
                loading={requestedChange}
              />
            )}
          </CenteredFormContainer>
        </form>
      )}
    />
  );
}

const CenteredFormContainer = styled(FormContainer)`
  width: auto;
  display: flex;
  flex-flow: column;
  align-items: ${({isOrderDetailsCheckout}) =>
    isOrderDetailsCheckout ? "flex-start" : "center"};
  > *:not(:last-child) {
    margin-bottom: ${({theme, isOrderDetailsCheckout}) =>
      isOrderDetailsCheckout ? theme.v2.spacing(2) : theme.v2.spacing(8)};
  }
`;

export const TextAreaInput = styled.textarea`
  width: 100%;
  background-color: #fafafa;
  color: #343434;
  border: 1px solid #ebebeb;
  box-sizing: border-box;
  border-radius: ${({theme}) => theme.v2.decoration.border.radius.default};
  font-family: ${({theme}) => theme.v2.typography.body.family};
  font-weight: ${({theme}) => theme.v2.typography.body.weight.light};
  font-size: ${({theme}) => theme.v2.typography.sizing.m.lg};
  line-height: 17px;
  padding: 10px;
  resize: none;
`;

export default OrderRatingForm;
